import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ErrorMessage, Formik, useFormikContext } from "formik";
import { FC, FormEvent, memo, useContext, useEffect, useState } from "react";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import ScrollToFirstError from "../../../common-components/ScrollToFirstError";
import { AUBusinessTravelContext } from "../../AUBusinessTravel";
import btConstants from "../../config/bt-constants";
import { IAUBusinessTravelContext, IConfirmation } from "../../config/interfaces";
import Popup from "../Popup";
import QuoteApi from "../api/QuoteApi";
import Footer from "../common/Footer";
import QuoteSummary from "../common/QuoteSummary";
import CardIcon from "../icons/CardIcon";
import InvoiceIcon from "../icons/InvoiceIcon";
import LoadingModal from "../modal/LoadingModal";
import ShowDutiesModal from "../modal/ShowDutiesModal";

interface OrderFormProps {}
const stripePromise = loadStripe(btConstants.BTpublishableKey!);

const PaymentForm: FC<OrderFormProps> = (props: OrderFormProps) => {
  const { order, previous, version, quoteData } = useContext(
    AUBusinessTravelContext
  ) as IAUBusinessTravelContext;

  const [loading, setLoading] = useState<boolean>(false);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);

  const [showDutiesModal, setShowDutiesModal] = useState<boolean>(false);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response: any = await QuoteApi.getPaymentIntent(order.order_number);
      setStripeIntentData(response.data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const initialFormValues: IConfirmation = {
    acceptAgreement: false,
    acceptDisclosure: false,
    // subscriptionPreferences: false,
  };

  const validationSchema = yup.object().shape({
    acceptAgreement: yup.boolean().required().oneOf([true], "You must agree the  statement."),
    acceptDisclosure: yup.boolean().required().oneOf([true], "You must accept disclosure & the terms and conditions."),
    // subscriptionPreferences: yup.boolean().notRequired(),
  });

  const handleFormSubmit = async (values: IConfirmation) => {
    try {
      setLoading(true);
    } catch (e: any) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Popup
          onClose={() => {
            // setShowLoadingModal(false);
          }}
          title=""
          children={<LoadingModal />}
          closeButton={false}
          LoadingModal={true}
        />
      )}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, touched, errors, setFieldValue, handleBlur }) => (
          <div className={`${appStyles.mainContainer} ${appStyles.btMainContainer}`}>
            <span
              className={appStyles.backIcon}
              onClick={() => {
                previous();
              }}
            />
            <div className={`${appStyles.quoteResultWrapper} ${appStyles.btQuoteResultWrapper}`}>
              <div className={`${appStyles.quoteDetailsBox} ${appStyles.businessBorderBox}`}>
                <h3 className={appStyles.btTitle}>Confirmation</h3>

                <div className={` ${appStyles.quoteFormOtherTravelerInformation}`}>
                  <div className={appStyles.formRow}>
                    <p className={`${appStyles.formLabelHelper} ${appStyles.btFormLabelHelper}`}>
                      <br />
                      On behalf of the company, I confirm, understand and agree to the following statements: 
                    </p>
                  </div>
                  <div className={appStyles.formRow}>
                    <ul className={`${appStyles.formLabelHelper} ${appStyles.btFormHelperList}`}>
                      <li>Manual work is not covered under this policy;</li>
                      <li>
                        Charter and non-scheduled flights are not covered under this policy;
                      </li>
                      <li>
                        Employees aged 70 years or older at the start date are not covered under this policy;
                      </li>
                      <li>
                        The company will advise battleface if we are likely to exceed the number of trips selected for this policy by more than 10%, and this may result in an increased premium to cover all the eligible trips.
                      </li>

                    </ul>
                  </div>
                  {/* first  */}
                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.formBlock} ${appStyles.btFormBlock}`}>
                      <div
                        className={`${appStyles.formField} ${
                          errors.acceptAgreement && touched.acceptAgreement && appStyles.hasError
                        }`}
                      >
                        <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>

                          <label
                            htmlFor="acceptAgreement"
                            className={`${appStyles.formLabelHelper}
                                                          ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="acceptAgreement"
                              name="acceptAgreement"
                              checked={values.acceptAgreement}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("acceptAgreement", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>By checking here, I confirm that I understand and agree with the above, and that I have read and understood the{" "}
                            <a
                              className={appStyles.btLink}
                              href={"https://www.battleface.com/wp-content/uploads/2026/03/bf17_Business-Travel-PDS_010326.pdf"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              PDS
                            </a>
                            ,{" and "}
                            <a
                              className={appStyles.btLink}
                              href={"https://www.battleface.com/wp-content/uploads/2026/03/TMD_bf17-Bus-Tvl-240226-final-1.pdf"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              TMD
                            </a>
                            , including any{" "}
                            <a
                              className={appStyles.btLink}
                              href={"https://www.smartraveller.gov.au/"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              travel alerts relevant to trips to be undertaken by our employees.
                            </a>
                          </label>

                          {/* <label
                            htmlFor="acceptAgreement"
                            className={`${appStyles.formLabelHelper}
                             ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="acceptAgreement"
                              name="acceptAgreement"
                              checked={values.acceptAgreement}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("acceptAgreement", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>I have read and
                            confirm the above statements.
                            <span className={`${appStyles.btStartIcon}`}>*</span>
                          </label> */}
                          {/* <ErrorMessage name="acceptAgreement">
                            {(msg) => <div className={appStyles.formError}>{msg}</div>}
                          </ErrorMessage> */}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* second ? */}

                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.formBlock} ${appStyles.btFormBlock}`}>
                      <div
                        className={`${appStyles.formField} ${
                          errors.acceptDisclosure && touched.acceptDisclosure && appStyles.hasError
                        }`}
                      >
                        <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>
                          <label
                            htmlFor="acceptDisclosure"
                            className={`${appStyles.formLabelHelper}
                                                          ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="acceptDisclosure"
                              name="acceptDisclosure"
                              checked={values.acceptDisclosure}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("acceptDisclosure", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>I confirm that I have read and accepted my{" "}
                            <a
                              className={`${appStyles.bfSummaryLinkButton}`}
                              style={{ textDecoration: "underline", cursor: "pointer" }}
                              rel="noopener noreferrer"
                              onClick={() => setShowDutiesModal(true)}
                            >
                              Duty to take reasonable care not to make a misrepresentation.
                            </a>
                          </label>
                          {/* <ErrorMessage name="acceptDisclosure">
                            {(msg) => <div className={appStyles.formError}>{msg}</div>}
                          </ErrorMessage> */}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                <hr className={appStyles.btHrStyle} />

                <div className={`${appStyles.formBlock} ${appStyles.bfMarginTop}`}>
                  {!loading && stripeIntentData && (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: stripeIntentData.client_secret,
                      }}
                    >
                      <PaymentFormElement />
                    </Elements>
                  )}
                </div>
                <Footer />
              </div>

              <QuoteSummary Premium={true} />
            </div>
          </div>
        )}
      </Formik>

      {showDutiesModal && (
        <Popup
          onClose={() => {
            setShowDutiesModal(false);
          }}
          title=""
          styleBenefitModal={true}
          children={<ShowDutiesModal />}
        />
      )}

    </>
  );
};

export default memo(PaymentForm);

type FormProps = {};

const PaymentFormElement: FC<FormProps> = (props: FormProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { handleSubmit, validateForm, values, submitCount, errors, isValid } = useFormikContext<IConfirmation>();

  const { next, order, setLoader, loader, version } = useContext(
    AUBusinessTravelContext
  ) as IAUBusinessTravelContext;

  const stripe = useStripe();
  const elements: any = useElements();

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    setLoader("payment");
    event.preventDefault();
    await validateForm();
    handleSubmit();
    const isError: any = await validateForm(values);

    if (Object.keys(isError).length > 0) {
      setLoader(null);
      return;
    }


    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      setLoader(null);
      return;
    }

    setIsProcessingPayment(true);

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: "",
      },
    });

    // // This point will only be reached if there is an immediate error when
    // // confirming the payment. Otherwise, your customer will be redirected to
    // // your `return_url`. For some payment methods like iDEAL, your customer will
    // // be redirected to an intermediate site first to authorize the payment, then
    // // redirected to the `return_url`.

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
      setLoader(null);
      setIsProcessingPayment(false);
      return;
    } else {
      setIsProcessingPayment(false);
      setLoader(null);
      next();
    }
    
  };

  return (
    <>
      {loader && (
        <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}

      <form onSubmit={handlePaymentSubmit}>
        <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
        <div className={` ${appStyles.formBox} ${appStyles.formBlock}`}>
          <div className={appStyles.pageSubTitle}>
            <h2 className={appStyles.colorPrimary}>Payment Information</h2>
          </div>
          <div className={` ${appStyles.btPaymentInfoTabWrap}`}>
            <div
              className={`${appStyles.btPaymentInfoTab}  ${appStyles.btPaymentInfoTabActive}`}
            >
              <CardIcon /> Card
            </div>

          </div>
          {(
            <div className={`${appStyles.formRow} ${appStyles.paymentCardWrapper}`}>
              <PaymentElement
                onReady={() => {
                  setIsStripeLoading(false);
                }}
              />
            </div>
          )}
        </div>

        {!isStripeLoading && (
          <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
            <div className={`${appStyles.buttonBlock} `}>
              <button
                className={`${appStyles.bfButton} ${appStyles.btButton100} ${appStyles.mT24} `}
                type="submit"
                disabled={isProcessingPayment || !stripe || !elements}
              >
                {"Confirm and pay"}
              </button>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className={appStyles.formRow}>
            <div className={appStyles.battlefaceServerError}>{errorMessage}</div>
          </div>
        )}

      </form>
    </>
  );
};
